import lodash from 'lodash';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getFilteredMenuItemCount from '../selectors/getFilteredMenuItemCount';
import MenuCategoriesBar from '../components/MenuCategoriesBar';
import MenuCategory from '../components/MenuCategory';
import MenuLocationDetails from '../components/MenuLocationDetails';
import RedirectAndPreserveSearch from '../components/RedirectAndPreserveSearch';
import ScreenHero from '../components/ScreenHero';
import ScrollAnchor from '../components/ScrollAnchor';
import Text from '../components/Text';
import ViewCartButton from '../components/ViewCartButton';
import { MENU_TOP_ANCHOR } from '../constants';
import {
  COMPLETED_ORDER_SCREEN_ROUTE,
  INITIAL_SCREEN_ROUTE,
  REVIEW_ORDER_SCREEN_ROUTE,
} from '../hooks/useRoutes';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { PROOF_OF_AGE_MODAL } from '../modals/AgeProofModal';
import { MESSAGE_MODAL_ID } from '../modals/MessageModal';
import EnergyText from '../components/EnergyText';
import { addStagedPurchases } from '../libs/polygon-ordering/src/reducers/currentOrder/stagedPurchases';
import { useLocation } from 'react-router-dom';
import { ORDER_SUMMARY_MODAL_ID } from '../modals/OrderSummaryModal';
import { UPSELL_MODAL } from '../modals/Upsells';
import { MENU_ADVERTISEMENT_MODAL } from '../modals/MenuAdvertisementModal';
import { ITEM_CART_AVAILABILITY_MODAL } from '../modals/ItemCartAvailabilityModal';
import getMenuOverlayAdvertisements from '../selectors/getMenuOverlayAdvertisements';
import getOrderInProgress from '../selectors/getOrderInProgress';
import getThemeLookup from '../selectors/getThemeLookup';
import { setCurrentModalId } from '../slices/currentModalId';
import { setMenuMessageModalDisplayed } from '../slices/menuMessageModalDisplayed';
import { setReorderableItems } from '../slices/reorderableItems';
import { setCurrentModal } from '../thunks/setCurrentModal';
import combineStyles from '../utils/combineStyles';
import debug from '../utils/debug';
import { getHeight } from '../utils/screen';
import { TEXT_PROPERTIES } from '../utils/theme';
import Footer from '../components/Footer';
import { categoryContainsAlcoholicItems } from '../libs/polygon-ordering/src/utils/ordering/filterAlcoholic';
import { viewCart } from '../utils/analytics';
import { deliveryFee } from '../thunks/deliveryFee';
import { refreshOrderingProviderDetails } from '../thunks/refreshOrderingProviderDetails';
import { shouldShowPresaleModal } from '../utils/shouldShowPresaleModal';
import { openPresaleModal } from '../utils/presaleModalRef';

const {
  getOrderSubmitted,
  getMenuFilterTags,
  getAgeProofSubmitted,
  getLocationBrandsWithMenuRepresentation,
  getFilteredRootCategory,
  getStagedPurchaseCount,
  getEnabledUpsellByCheckout,
  getResetEnableUpsellByCheckout,
  getUpsellsByCheckout,
  getUpsellTitle,
  getEnableTCSFooter,
  getStagedPurchases,
  getOrderTotals,
  getLocation,
  getSaleType,
  getBrands,
} = OrderingSelectors;
const { fetchStockBalances } = OrderingOperations;

export const CurrentCategoryContext = createContext<string | null>(null);

const MenuScreen = () => {
  const [scrollTop, setScrollTop] = useState<any>(null);
  const [categoryRefs, setCategoryRefs] = useState<{ [key: string]: any }>({});
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const rootCategory = useAppSelector(getFilteredRootCategory);
  const orderSubmitted = useAppSelector(getOrderSubmitted);
  const itemCount = useAppSelector(getFilteredMenuItemCount);
  const orderInProgress = useAppSelector(getOrderInProgress);
  const [currentCategoryId, setCurrentCategoryId] = useState('MENU_TOP_ANCHOR');
  const [adsShownThisVisit, setAdsShownThisVisit] = useState(false);
  const [messageShownThisVisit, setMessageShownThisVisit] = useState(false);
  const [ageProofShownThisVisit, setAgeProofShownThisVisit] = useState(false);
  const currentModalId = useAppSelector(state => state.currentModalId);
  const adjustOrderInProgress = useAppSelector(
    state => state.loadingDependencies.handleAdjustOrderInProgress,
  );
  const apiHandlerReady = useAppSelector(state => state.apiHandlerReady);

  //const currentCategoryId = useAppSelector(getCurrentCategoryId);
  const menuCategoriesBarHeight = useAppSelector(state => state.menuCategoriesBarHeight);
  const dispatch = useAppDispatch();
  const enableStockPolling = useAppSelector(state => state.config.enableStockPolling);
  const stockPollingInterval = useAppSelector(state => state.config.stockPollingInterval);
  const [checkPLUQuantityOnLoad, setCheckPLUQuantityOnLoad] = useState(true);
  const interval = useRef<NodeJS.Timeout | null>(null);
  const filterTags = useAppSelector(getMenuFilterTags);
  const ageProofSubmitted = useAppSelector(getAgeProofSubmitted);
  const representedBrands = useAppSelector(getLocationBrandsWithMenuRepresentation);
  const multipleBrands = representedBrands.length > 1;

  const reorderableItems = useAppSelector(state => state.reorderableItems);
  const stagedPurchaseCount = useAppSelector(getStagedPurchaseCount) as number;

  const enableUpsellByCheckout = useAppSelector(getEnabledUpsellByCheckout);
  const resetEnableUpsellByCheckout = useAppSelector(getResetEnableUpsellByCheckout);

  const upsells: (Item & { upsellAvailable: boolean })[] = useAppSelector(getUpsellsByCheckout);
  const upsellTitle = useAppSelector(getUpsellTitle);

  const menuOverlayAdvertisements = useAppSelector(getMenuOverlayAdvertisements);
  const showTCS = useAppSelector(getEnableTCSFooter);

  const browserLocation = useLocation();

  const stagedPurchases = useAppSelector(getStagedPurchases);
  const brands = useAppSelector(getBrands);
  const orderTotals = useAppSelector(getOrderTotals);
  const location = useAppSelector(getLocation);
  const saleType = useAppSelector(getSaleType);
  const disableFetchDeliveryFeeApi = useAppSelector(state => state.disableFetchDeliveryFeeApi);

  useEffect(() => {
    if (!disableFetchDeliveryFeeApi && saleType === OrderingConstants.SALE_TYPE.DELIVERY) {
      const timer = setTimeout(() => {
        dispatch(deliveryFee()).unwrap();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    disableFetchDeliveryFeeApi,
    saleType,
    JSON.stringify(stagedPurchases),
    JSON.stringify(location),
  ]);

  const handleViewCart = () => {
    dispatch(refreshOrderingProviderDetails());

    dispatch(
      setCurrentModal({
        modalId: ORDER_SUMMARY_MODAL_ID,
      }),
    );

    viewCart({
      items: stagedPurchases.map(purchaseItem => {
        const category =
          categories.find(category =>
            lodash.map(category.items, 'id').find(id => id === purchaseItem.item.id),
          )?.name || null;
        const brand = brands.find(({ id }) => purchaseItem.brandId === id)?.name || null;
        return {
          ...(purchaseItem.item.sizes.find(item => item.id == purchaseItem.sizeId) ||
            purchaseItem.item),
          quantity: purchaseItem.quantity,
          totalPrice: purchaseItem.moneyPrice,
          choicesWithQuantity: purchaseItem.choicesWithQuantity,
          brand,
          category,
        };
      }),
      total: orderTotals!.discountedMoneyPrice,
      store: location?.name,
      saleType,
    });

    if (enableUpsellByCheckout && resetEnableUpsellByCheckout && upsells.length > 0) {
      dispatch(
        setCurrentModal({
          modalId: UPSELL_MODAL,
          preserveReturnModalDetails: true,
          params: {
            items: upsells,
            title: upsellTitle ? upsellTitle : t('title.modal.upsellTitle'),
            checkoutUpsell: true,
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (stagedPurchaseCount !== 0 && browserLocation?.state?.from === REVIEW_ORDER_SCREEN_ROUTE) {
      handleViewCart();
    }
  }, [browserLocation]);

  const prevAgeProofSubmittedRef = useRef(ageProofSubmitted);
  useEffect(() => {
    if (prevAgeProofSubmittedRef.current && !ageProofSubmitted) {
      setAgeProofShownThisVisit(false);
    }
    prevAgeProofSubmittedRef.current = ageProofSubmitted;
  }, [ageProofSubmitted]);

  let menuHasAlcohol = false;
  if (rootCategory?.subCategories) {
    menuHasAlcohol = rootCategory.subCategories.some(category =>
      categoryContainsAlcoholicItems(category),
    );
  }

  useEffect(() => {
    if (!rootCategory || adjustOrderInProgress) return;

    const menuHasAds = menuOverlayAdvertisements && menuOverlayAdvertisements.length;

    if (menuHasAlcohol && !ageProofSubmitted && !ageProofShownThisVisit) {
      setAgeProofShownThisVisit(true);
      dispatch(setCurrentModal(PROOF_OF_AGE_MODAL));
    } else if (!adsShownThisVisit && menuHasAds && (!menuHasAlcohol || ageProofSubmitted)) {
      setAdsShownThisVisit(true);
      dispatch(setCurrentModal(null));
      dispatch(setCurrentModalId(MENU_ADVERTISEMENT_MODAL));
    } else if (
      !messageShownThisVisit &&
      (!menuHasAlcohol || ageProofSubmitted) &&
      currentModalId !== MENU_ADVERTISEMENT_MODAL &&
      currentModalId !== ITEM_CART_AVAILABILITY_MODAL &&
      (adsShownThisVisit || !menuHasAds)
    ) {
      setMessageShownThisVisit(true);
      showMessageModals();
    }

    if (location?.id && shouldShowPresaleModal(location.id, saleType)) {
      openPresaleModal();
    }
  }, [
    rootCategory,
    menuOverlayAdvertisements,
    adsShownThisVisit,
    messageShownThisVisit,
    ageProofShownThisVisit,
    currentModalId,
    adjustOrderInProgress,
    ageProofSubmitted,
  ]);

  const showProofOfAgeModal = () => {
    if (menuHasAlcohol && !ageProofSubmitted) {
      setTimeout(() => {
        dispatch(setCurrentModal(PROOF_OF_AGE_MODAL));
      });
    }
  };

  const showMessageModals = () => {
    if (multipleBrands) {
      setTimeout(() =>
        dispatch(
          setCurrentModal({
            modalId: MESSAGE_MODAL_ID,
            params: {
              title: t('title.modal.brandsEducation'),
              message: t('brandsEducationMessage'),
              action: () => {
                if (menuHasAlcohol && !ageProofSubmitted) {
                  showProofOfAgeModal();
                } else {
                  dispatch(setCurrentModal(null));
                  dispatch(setMenuMessageModalDisplayed(true));
                }
              },
            },
            clearReturnModalDetails: true,
          }),
        ),
      );
    } else {
      showProofOfAgeModal();
    }
  };

  useEffect(() => {
    if (checkPLUQuantityOnLoad) {
      if (apiHandlerReady) dispatch(fetchStockBalances());
      setCheckPLUQuantityOnLoad(false);
    }

    if (enableStockPolling && stockPollingInterval) {
      interval.current = setInterval(
        () => dispatch(fetchStockBalances()),
        stockPollingInterval * 1000,
      );
      return () => {
        clearInterval(interval.current as NodeJS.Timeout);
        dispatch(setMenuMessageModalDisplayed(true));
      };
    }
  }, [apiHandlerReady]);

  const reportRef = (categoryId: string, ref: any) => {
    setCategoryRefs(prevState => {
      return { ...prevState, [categoryId]: ref };
    });
  };

  const scrollToCategory = (categoryId: string, offset?: number) => {
    const ADDITIONAL_ADJUSTMENT = deviceTypeMobile ? 90 : 30; // magic number changes if you use psb

    const categoryRef = categoryRefs[categoryId];

    setScrollTop(
      (categoryRef?.current?.offsetTop || 0) -
        Number(menuCategoriesBarHeight) -
        ADDITIONAL_ADJUSTMENT +
        (offset || 0),
    );
  };

  const setCurrentCategoryIdAndScroll = (newCurrentCategoryId: string) => {
    if (currentCategoryId !== newCurrentCategoryId) {
      setCurrentCategoryId(newCurrentCategoryId);
    }

    scrollToCategory(newCurrentCategoryId);
  };

  const onScroll = () => {
    try {
      if (scrollTop != null) {
        setScrollTop(null);
      }

      // distance from top of screen (or is it the scroll container?)
      // magic number (based on feel + avg/min height of menu items)
      const THRESHOLD = 250;

      const categoryBottomPositions = lodash
        .chain(categoryRefs)
        .toPairs()
        .map(([categoryId, ref]) =>
          ref?.current
            ? {
                categoryId,
                bottom: ref.current.getBoundingClientRect().bottom,
              }
            : undefined,
        )
        .filter()
        .orderBy('bottom', 'asc')
        .value();

      let newCurrentCategoryId;

      const firstBelowThreshold = categoryBottomPositions.find(
        (bar: any) => bar.bottom > THRESHOLD,
      );
      const last = categoryBottomPositions[categoryBottomPositions.length - 1];

      if (firstBelowThreshold) {
        newCurrentCategoryId = firstBelowThreshold.categoryId;
      } else if (last) {
        newCurrentCategoryId = last.categoryId;
      }

      if (newCurrentCategoryId == null) {
        return;
      }

      if (newCurrentCategoryId !== currentCategoryId) {
        setCurrentCategoryId(newCurrentCategoryId);
      }
    } catch (e) {
      debug(false, 'menu onScroll encountered an problem', e);
    }
  };

  useEffect(() => {
    onScroll();
  }, []);

  const setCartWithReorderableItems = (items: _Purchase[]) => {
    dispatch(addStagedPurchases(items));
    dispatch(setReorderableItems(null));
  };

  //If the user pressed "Reorder" then need to populate the cart with the previously ordered items.
  useEffect(() => {
    if (reorderableItems !== null) {
      //Don't know why but this timeout is required. Otherwise the cart doesn't get populated.
      //@ts-ignore
      setTimeout(() => setCartWithReorderableItems(reorderableItems), 1000);
    }
  }, []);

  if (orderSubmitted) {
    return <RedirectAndPreserveSearch to={COMPLETED_ORDER_SCREEN_ROUTE} />;
  }

  if (!orderInProgress) {
    return <RedirectAndPreserveSearch to={INITIAL_SCREEN_ROUTE} />;
  }

  const categories = rootCategory?.subCategories || [];

  return (
    <ScreenHero
      onScroll={lodash.throttle(onScroll, 300)}
      scrollTop={scrollTop}
      heroChildren={!deviceTypeMobile && <MenuLocationDetails />}
      heroBottom={<ViewCartButton handleViewCart={handleViewCart} />}
      themeKey="menuScreenParent"
    >
      <CurrentCategoryContext.Provider value={currentCategoryId}>
        <MenuCategoriesBar
          setCurrentCategoryId={setCurrentCategoryIdAndScroll}
          jumpToTop={() => setTimeout(() => scrollToCategory(MENU_TOP_ANCHOR, 0), 100)}
        />

        <div
          style={combineStyles(
            deviceTypeMobile ? styles.mainContainerMobile : styles.mainContainerDesktop,
            p('screen', ['backgroundColor']),
            p('menuScreen', ['backgroundColor']),
          )}
        >
          <ScrollAnchor reportRef={reportRef} identifier={MENU_TOP_ANCHOR} />

          {itemCount ? (
            categories.map((category, index) => (
              <MenuCategory
                key={category.id}
                category={category}
                reportRef={reportRef}
                shrinkMargin={index === categories.length - 1}
              />
            ))
          ) : (
            <div style={combineStyles(styles.noResults, { minHeight: getHeight() })}>
              <Text
                themeKey="noResultsInMenuSearchMessage"
                value={t('noResultsInMenuSearchMessage')}
              />
              {filterTags && filterTags.length > 0 && (
                <Text
                  style={{ marginTop: 10 }}
                  themeKey="removeFiltersWarnings"
                  value={t('removeFiltersWarnings')}
                />
              )}
            </div>
          )}

          {!!itemCount && (
            <div style={{ textAlign: 'center' }}>
              <EnergyText
                style={combineStyles({ fontSize: 12 }, p('energyIntakeText', TEXT_PROPERTIES))}
                themeKey="energyIntakeText"
              />
            </div>
          )}
        </div>
      </CurrentCategoryContext.Provider>

      {showTCS && <Footer />}
    </ScreenHero>
  );
};

const styles = {
  mainContainerDesktop: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: 20,
    maxWidth: 1600,
    margin: '2.5em auto 28px auto',
  },
  mainContainerMobile: {
    flex: 1,
    padding: '35px 25px 28px 25px',
    marginBottom: '20px',
  },

  noResults: {
    paddingTop: 30,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};

export default MenuScreen;
